import {
  useCallback,
  useRef,
  type SyntheticEvent
} from 'react';
import {
  useQueryClient,
  type QueryClient,
  type QueryKey
} from '@tanstack/react-query';

const MEDIA_REFRESH_COOLDOWN_MS = 5_000;
const refreshTimes = new WeakMap<QueryClient, Map<string, number>>();

export type MediaErrorHandler = () => void;

export function useMediaRecovery(queryKeys: readonly QueryKey[]): MediaErrorHandler {
  const queryClient = useQueryClient();
  const queryKeysRef = useRef(queryKeys);
  queryKeysRef.current = queryKeys;

  return useCallback(() => {
    const now = Date.now();
    let clientRefreshTimes = refreshTimes.get(queryClient);
    if (!clientRefreshTimes) {
      clientRefreshTimes = new Map();
      refreshTimes.set(queryClient, clientRefreshTimes);
    }

    for (const queryKey of queryKeysRef.current) {
      const cooldownKey = JSON.stringify(queryKey);
      const lastRefreshAt = clientRefreshTimes.get(cooldownKey);
      if (lastRefreshAt !== undefined && now - lastRefreshAt < MEDIA_REFRESH_COOLDOWN_MS) {
        continue;
      }
      clientRefreshTimes.set(cooldownKey, now);
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient]);
}

export function handleMediaLoad(event: SyntheticEvent<HTMLImageElement>) {
  delete event.currentTarget.dataset.mediaRecoveryAttempted;
}

export function handleMediaError(
  event: SyntheticEvent<HTMLImageElement>,
  onMediaError?: MediaErrorHandler
) {
  if (event.currentTarget.dataset.mediaRecoveryAttempted === 'true') {
    return;
  }
  event.currentTarget.dataset.mediaRecoveryAttempted = 'true';
  onMediaError?.();
}
